import React, { ComponentProps, ForwardedRef } from 'react';
import { prepareProps } from '../../utils/propUtils';
import { classes } from '../../utils/utils';
import { DropdownListItem } from '../DropdownListItem/DropdownListItem';
import styles from './DropdownList.module.css';

export type DropdownListProps<T> = Omit<ComponentProps<'div'>, 'ref'> & {
  items: Array<T>;
  highlightedItem?: T;
  getDisplayTextOfItem: (item: T) => string;
  getKeyOfItem: (item: T, index: number) => string;
  onItemSelected: (item: T) => void;
};

// eslint-disable-next-line react/no-multi-comp
export const DropdownList = React.memo(
  React.forwardRef(
    <T,>(props: DropdownListProps<T>, ref: ForwardedRef<HTMLDivElement>) => {
      const {
        items,
        highlightedItem,
        getDisplayTextOfItem,
        getKeyOfItem,
        onItemSelected,
        className,
        ...remainingProps
      } = prepareProps({ props, ignoreChildren: true });

      return (
        <div
          ref={ref}
          {...remainingProps}
          className={classes(className, styles.list)}
        >
          {items.map((item, index) => (
            <DropdownListItem<T>
              item={item}
              highlighted={item === highlightedItem}
              getDisplayTextOfItem={getDisplayTextOfItem}
              key={getKeyOfItem(item, index)}
              onItemSelected={onItemSelected}
              data-testid="DropdownList.Item"
            ></DropdownListItem>
          ))}
        </div>
      );
    }
  )
) as <T>(
  props: DropdownListProps<T> & { ref?: ForwardedRef<HTMLDivElement> }
) => JSX.Element;
